import { ReactNode, useCallback, useState, FC } from 'react';
import { Box, FelaCSS } from '@bridebook/ui/src';
import IconTickRound from '@bridebook/ui/src/components/svg/icon-tick-round';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';
import { IconCopy } from '@bridebook/ui/src/icons';
import { TColor } from '@bridebook/ui/src/types';
import useCopyToClipboard from '@bridebook/ui/src/utils/hooks/use-copy-to-clipboard';
import componentStyles from './copy-to-clipboard.style';

interface IProps {
  /**
   * Optional function to render the icon when the text is not copied
   * @param color color of the icon
   * @param width width of the icon
   * @returns ReactNode with the icon
   * @default IconCopy with color and width from props
   */
  renderIcon?: (color: TColor, width: number) => ReactNode;
  /**
   * Optional function to render the icon when the text is copied
   * @param color color of the icon
   * @param width width of the icon
   * @returns ReactNode with the icon
   * @default IconTickRound with color and width from props
   */
  renderIconClicked?: (color: TColor, width: number) => ReactNode;
  /**
   * Timeout in milliseconds to show the copied icon
   * @default 2000
   */
  timeout?: number;
  /**
   * Text to be copied to the clipboard
   */
  text: string;
  /**
   * Color of the icon
   * @default space60
   */
  color?: TColor;
  /**
   * Width of the icon (in pixels)
   * @default 16
   */
  width?: number;
  /**
   * Custom styles for the wrapper component
   */
  style?: FelaCSS;
}

export const CopyToClipboard: FC<IProps> = ({
  renderIcon,
  renderIconClicked,
  text,
  color = 'space60',
  width = 16,
  timeout = 2000,
  style = {},
}) => {
  const { copy } = useCopyToClipboard();
  const [isCopiedSuccessfully, setIsCopiedSuccessfully] = useState(false);

  const handleClick = useCallback(async () => {
    const success = await copy(text);
    setIsCopiedSuccessfully(success);

    if (success) {
      setTimeout(() => setIsCopiedSuccessfully(false), timeout);
    }
  }, [copy, text, timeout]);

  return (
    <Box as="a" style={mergeStyles([componentStyles.wrapper, style])} onClick={handleClick}>
      {isCopiedSuccessfully ? (
        renderIconClicked ? (
          renderIconClicked(color, width)
        ) : (
          <IconTickRound color={color} width={width} />
        )
      ) : renderIcon ? (
        renderIcon(color, width)
      ) : (
        <IconCopy color={color} width={width} />
      )}
    </Box>
  );
};
